import { defineStore, storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useBibAssignStoreRef, useContestantStoreRef } from '.';
import WellKnown from '@/objects/generated/static/WellKnown';
import type { ContestantBase } from '@/objects/generated';
import { EMITIsValid } from '@/utilities/participantIdUtils';

export const useValidationStore = defineStore('validation', () => {
    // TODO: Move all checks from EventDashboardRaceSetupMessages.vue here

    const { VisibleContestants } = useContestantStoreRef();
    const { BibAssigns } = useBibAssignStoreRef();

    /**
     * If we have autoassignemittags we shold show warning if multiple contestants have the same tag
     */
    const hasDuplicateEmitTags = computed(() => {
        const tagSet = new Set<string>();
        for (const contestant of VisibleContestants.value) {
            const emittag = contestant.ParticipantIDs.find((id) => id.ParticipantIDTypeID == WellKnown.IDTYPE_EMIT);
            if (!emittag || !emittag.Value) {
                continue;
            }
            if (tagSet.has(emittag.Value)) {
                return true;
            }
            tagSet.add(emittag.Value);
        }
        return false;
    });

    /**
     * If we have autoassignemittags we shold show warning if some tags are not valid EMIT IDs
     */
    const hasInvalidEmitTags = computed(() => {
        for (const contestant of VisibleContestants.value) {
            const emittag = contestant.ParticipantIDs.find((id) => id.ParticipantIDTypeID == WellKnown.IDTYPE_EMIT);
            if (!emittag || !emittag.Value) {
                continue;
            }
            // make number
            const emittagValueNumber = Number(emittag.Value);
            if (isNaN(emittagValueNumber)) {
                return true;
            }
            if (EMITIsValid(emittagValueNumber) === false) {
                return true;
            }
        }
        return false;
    });

    /** List of invalid emit tags */
    const invalidEmitTags = computed(() => {
        const invalidTags: Map<string, ContestantBase> = new Map();
        for (const contestant of VisibleContestants.value) {
            const emittag = contestant.ParticipantIDs.find((id) => id.ParticipantIDTypeID == WellKnown.IDTYPE_EMIT);
            if (!emittag || !emittag.Value) {
                continue;
            }
            // make number
            const emittagValueNumber = Number(emittag.Value);
            if (isNaN(emittagValueNumber) || EMITIsValid(emittagValueNumber) === false) {
                invalidTags.set(emittag.Value, contestant);
            }
        }
        return invalidTags;
    });

    /**
     * Get a map of duplicate emit tags to the contestants that have them
     */
    const duplicateEmitTags = computed(() => {
        const duplicates = new Map<string, ContestantBase[]>();
        for (const contestant of VisibleContestants.value) {
            const emittag = contestant.ParticipantIDs.find((id) => id.ParticipantIDTypeID == WellKnown.IDTYPE_EMIT);
            if (!emittag || !emittag.Value) {
                continue;
            }
            if (duplicates.has(emittag.Value)) {
                duplicates.get(emittag.Value)?.push(contestant);
            } else {
                duplicates.set(emittag.Value, [contestant]);
            }
        }
        // remove the ones with only one entry
        for (const [key, value] of duplicates) {
            if (value.length < 2) {
                duplicates.delete(key);
            }
        }
        return duplicates;
    });

    /**
     * If we have assigned bibs and some contestants have no bib assigned
     */
    const hasContestantsWithoutBib = computed(() => {
        return VisibleContestants.value.some((c) => c.Bib == null);
    });

    /**
     * If someone has a bib assigned
     */
    const bibsAssigned = computed(() => {
        return BibAssigns.value.length > 0;
    });

    /**
     * Number of contestants without a bib assigned
     */
    const numMissingBibs = computed(() => {
        return VisibleContestants.value.filter((c) => c.Bib == null).length;
    });

    return {
        HasDuplicateEmitTags: hasDuplicateEmitTags,
        DuplicateEmitTags: duplicateEmitTags,
        HasContestantsWithoutBib: hasContestantsWithoutBib,
        BibsAssigned: bibsAssigned,
        NumMissingBibs: numMissingBibs,
        HasInvalidEmitTags: hasInvalidEmitTags,
        InvalidEmitTags: invalidEmitTags,
    };
});

export const useValidationStoreRef = () => storeToRefs(useValidationStore());
